import { get, post } from '@/infra/rest';
import { UploadedFile } from "@/shared/types/file-upload";
export const downloadControlsTemplate = async (agentType: string) => {
  const response = await get<{
    type: string;
    signed_url: string;
    success: boolean;
  }>({
    url: `/v0/rag/workflows/download-template/${agentType}`,
  });
  
  if (response.success && response.signed_url) {
    // Create a temporary link to download the file
    const link = document.createElement('a');
    link.href = response.signed_url;
    link.download = `${response.type}_controls_template.xlsx`;
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
  } else {
    throw new Error("Invalid response from download template API");
  }
};

// Real API for importing controls
export const importControls = async (
  files: UploadedFile[], 
  agentType: string,
  sessionId: string
): Promise<{ success: boolean; message: string; importedCount?: number; controlsData?: any[]; signedUrl?: string }> => {
  const file = files[0];
  
  if (!file || file.status !== "success" || !file.signedUrl) {
    throw new Error("Invalid file provided or file not uploaded successfully");
  }

  try {
    // Step 1: Get controls URL from filled template (use existing signed URL)
    const requestData: {
      type: string;
      filled_template_signed_url: string;
      session_id?: string;
    } = {
      type: agentType,
      filled_template_signed_url: file.signedUrl
    };

    // Only include session_id if provided (skip for control set edit mode)
    if (sessionId && sessionId.trim() !== '') {
      requestData.session_id = sessionId;
    }

    const controlsResponse = await post<
      {
        type: string;
        filled_template_signed_url: string;
        session_id?: string;
      },
      {
        type: string;
        signed_url: string;
        success: boolean;
      }
    >({
      url: '/v0/rag/workflows/controls-url',
      data: requestData
    });

    if (!controlsResponse.success || !controlsResponse.signed_url) {
      throw new Error("Failed to process template");
    }

    // Step 3: Fetch the processed controls to count them
    const controlsFileResponse = await fetch(controlsResponse.signed_url);
    if (controlsFileResponse.ok) {
      const controlsText = await controlsFileResponse.text();
      let importedCount = 0;
      let controlsData: any[] = [];
      
      try {
        controlsData = JSON.parse(controlsText);
        importedCount = Array.isArray(controlsData) ? controlsData.length : 0;
      } catch {
        // If not JSON, assume it's CSV and count lines
        importedCount = controlsText.split('\n').filter(line => line.trim()).length - 1; // Subtract header
      }

      // For control set edit mode (empty sessionId), return the actual controls data
      const isControlSetEditMode = !sessionId || sessionId.trim() === '';
      
      return {
        success: true,
        message: "Controls imported successfully",
        importedCount,
        ...(isControlSetEditMode && { 
          controlsData: Array.isArray(controlsData) ? controlsData : [],
          signedUrl: controlsResponse.signed_url 
        })
      };
    }

    return {
      success: true,
      message: "Controls imported successfully",
      importedCount: 0
    };

  } catch (error) {
    console.error("Import error:", error);
    throw error;
  }
};

// API for saving controls as default organization template
export const saveAsDefaultControls = async (
  agentType: string,
  controlsUrl: string,
  sessionId: string
): Promise<{ success: boolean; message: string }> => {
  try {
    const response = await post<
      {
        type: string;
        controls_url: string;
        session_id: string;
      },
      {
        type: string;
        signed_url: string;
        success: boolean;
      }
    >({
      url: '/v0/rag/workflows/save-organization-controls',
      data: {
        type: agentType,
        controls_url: controlsUrl,
        session_id: sessionId
      }
    });

    if (!response.success) {
      throw new Error("Failed to save as default controls");
    }

    return {
      success: true,
      message: "Controls saved as default template successfully"
    };

  } catch (error) {
    console.error("Save as default error:", error);
    throw error;
  }
};

// API for saving a named control set
export const saveControlSet = async (
  name: string,
  controlsUrl: string,
  sessionId: string
): Promise<{ success: boolean; message: string }> => {
  try {
    const response = await post<
      {
        controls_url: string;
        controls_set_name: string;
        control_type: string;
      },
      {
        success: boolean;
        control_set_state: {
          controls_set_name: string;
          controls_url: string;
          default_control_set: boolean;
          id: string;
          control_set_published: boolean;
          created_by: string;
          created_on: string;
          updated_by: string;
          updated_on: string;
        };
      }
    >({
      url: '/v0/custom-control-set/save',
      data: {
        controls_url: controlsUrl,
        controls_set_name: name,
        control_type: 'multi_file_vendor_assessment'
      }
    });

    if (!response.success) {
      // Throw the response as error so the modal can access the full response
      throw response;
    }

    return {
      success: true,
      message: "Control set saved successfully"
    };

  } catch (error: any) {
    // If it's an HTTP error, throw the response data
    if (error?.response?.data) {
      throw error.response.data;
    }
    
    throw error;
  }
};
